import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { Badge, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { withPolling } from 'moet-core';

import { ContainerTemplate } from '@/components';
import { Audience, ExitPoint, ExitPointType } from '@/bpc/types/BpcPayload';
import { isFetchingBpc } from '@/selectors/loadingStates';
import {
  getBpcStatus,
  getBusinessAwareArrayTranslator,
  getBusinessAwareTranslator,
  getBusinessName,
  getContractOnboardingType,
  getExitPoints,
} from '@/selectors/bpc';

import { ContractOnboardingStatus } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingStatus';
import { ColoredIllustration } from './ColoredIllustration';
import { SmallIcon } from './SmallIcon';
import { Paragraphs, TextWithNewlines } from './Paragraphs';
import {
  getPollingInterval,
  SLOW_POLLING_INTERVAL,
  startPolling,
  STATUS_OVERRIDES,
  stopPolling,
  REDIRECT_TIMEOUT_IN_MILLISECONDS,
} from './constants';
import ExitPoints from './ExitPoints';
import { ContractOnboardingType } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingType';

type OnboardingStatusComponentProps = {
  error?: Record<string, unknown>;
  fetchOnboarding: () => Promise<void>;
  logExitPoint: (exitPoint: string) => void;
  logStatusEvent: (oldStatus: string | undefined, newStatus: string) => void;
};

type EnhancedOnboardingStatusComponentProps = OnboardingStatusComponentProps & {
  businessName: string;
  exitPoints?: ExitPoint[];
  status: ContractOnboardingStatus;
  contractOnboardingType: ContractOnboardingType;
  t: TranslatorFunction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tArray: ArrayTranslatorFunction<any>;
};

function makeNavigateToExitPointFn(
  exitPoints: ExitPoint[],
  setExitPointTranslationPath,
  logExitPoint,
) {
  return function navigateToExitPoint() {
    const relevantExitPoints = exitPoints?.filter(ep => ep.audience === Audience.WEB);

    if (!relevantExitPoints || !relevantExitPoints.length) {
      return;
    }

    const redirectExitPoint = relevantExitPoints.find(ep => ep.type === ExitPointType.REDIRECT);

    if (redirectExitPoint) {
      logExitPoint(redirectExitPoint.exit_point);
      window.location.href = redirectExitPoint.exit_point;
    } else {
      logExitPoint(relevantExitPoints[0].exit_point);
      setExitPointTranslationPath(relevantExitPoints[0].exit_point);
    }
  };
}

export const _OnboardingStatusComponent: React.FC<EnhancedOnboardingStatusComponentProps> = ({
  businessName,
  status,
  error,
  exitPoints = [],
  fetchOnboarding,
  t,
  tArray,
  logExitPoint,
  logStatusEvent,
  contractOnboardingType,
}: EnhancedOnboardingStatusComponentProps): ReactElement => {
  const tPrefix = useMemo(() => `group.status.${STATUS_OVERRIDES[status] || status}`, [status]);

  const [exitPointTranslationPath, setExitPointTranslationPath] = useState<string>();
  const [currentStatus, setCurrentStatus] = useState<ContractOnboardingStatus>();

  if (status !== currentStatus || !currentStatus) {
    logStatusEvent(currentStatus, status);
    setCurrentStatus(status);
  }

  const navigateToExitPoint = makeNavigateToExitPointFn(
    exitPoints,
    setExitPointTranslationPath,
    logExitPoint,
  );

  useEffect(() => {
    fetchOnboarding();
  }, []);

  useEffect(() => {
    if (
      (contractOnboardingType === ContractOnboardingType.UPSELL_WEBSTORE_TO_ADS_MANAGER &&
        status === ContractOnboardingStatus.ONBOARDING_COMPLETED) ||
      (contractOnboardingType === ContractOnboardingType.UPSELL_WEBSTORE_TO_CONTENT_MANAGER &&
        status === ContractOnboardingStatus.ONBOARDING_COMPLETED)
    ) {
      const timer = setTimeout(navigateToExitPoint, REDIRECT_TIMEOUT_IN_MILLISECONDS);
      return () => clearTimeout(timer);
    }
  }, [status, contractOnboardingType]);

  const exitPointsComponentProps = {
    status,
    exitPoints,
    exitPointTranslationPath,
    navigateToExitPoint,
    t,
    tPrefix,
  };

  return (
    <ContainerTemplate error={error}>
      <ColoredIllustration status={status as ContractOnboardingStatus} />

      <SpacerVertical spaceToken="space/150" />

      <Typography textToken="text-style/headings/titles/bold/primary">
        <TextWithNewlines>{t(`${tPrefix}.title`)}</TextWithNewlines>
      </Typography>

      <SpacerVertical spaceToken="space/150" />

      {t(`${tPrefix}.badge`) ? <Badge>{t(`${tPrefix}.badge`)}</Badge> : null}

      <SpacerVertical spaceToken="space/150" />

      <Paragraphs tKey={`${tPrefix}.paragraphs`} params={{ businessName }} tArray={tArray} />

      <SpacerVertical spaceToken="space/200" />

      <ExitPoints {...exitPointsComponentProps} />

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <SmallIcon status={status as ContractOnboardingStatus} />
        </div>

        <Typography textToken="text-style/text/super-paragraphs/body/bold">
          <TextWithNewlines> {t(`${tPrefix}.second_title`)}</TextWithNewlines>
        </Typography>
      </div>

      <SpacerVertical spaceToken="space/150" />

      <Paragraphs tKey={`${tPrefix}.second_paragraphs`} params={{ businessName }} tArray={tArray} />
    </ContainerTemplate>
  );
};

/* istanbul ignore next */
export const OnboardingStatusComponent = compose<
  EnhancedOnboardingStatusComponentProps,
  OnboardingStatusComponentProps
>(
  connect(state => ({
    isFetchingBpc: isFetchingBpc(state),
    status: getBpcStatus(state),
    businessName: getBusinessName(state),
    contractOnboardingType: getContractOnboardingType(state),
    exitPoints: getExitPoints(state),
    t: getBusinessAwareTranslator(state),
    tArray: getBusinessAwareArrayTranslator(state),
  })),
  withPolling(
    ({ fetchOnboarding }) => fetchOnboarding(),
    ({ isFetchingBpc: wasFetchingBpc }, { isFetchingBpc }) => wasFetchingBpc && !isFetchingBpc,
    {
      intervalCriteria: (_prevProps, { status }) => getPollingInterval(status),
      startCriteria: (_prevProps, { status }) => startPolling(status),
      exitCriteria: (_prevProps, { status }) => stopPolling(status),
      maxDelayTime: SLOW_POLLING_INTERVAL,
    },
  ),
)(_OnboardingStatusComponent);
